import { Link } from '@tanstack/react-router';
import type { CasinoGame } from '@/domain';
import { cn } from '@/lib/cn';
import { HorizontalScrollRow, Icon, Spinner, type IconName } from '@/ui/common';
import { GameTile } from './GameTile';

export interface GameCarouselProps {
  title: string;
  seeAllTo?: string;
  games: CasinoGame[];
  isLoading?: boolean;
  categoryOverride?: string;
  showFavorite?: boolean;
  onToggleFavorite?: (game: CasinoGame) => void;
  /** Optional leading icon in the section header (Figma CasinoSectionHeader). */
  icon?: IconName;
  /** Gold border + glow on tiles (Figma LiveTile). */
  tone?: 'default' | 'live';
  /** Hide the built-in header when a parent supplies one. */
  hideHeader?: boolean;
  className?: string;
}

export function GameCarousel({
  title,
  seeAllTo,
  games,
  isLoading = false,
  categoryOverride,
  showFavorite,
  onToggleFavorite,
  icon,
  tone = 'default',
  hideHeader = false,
  className,
}: GameCarouselProps) {
  if (!isLoading && games.length === 0) return null;

  return (
    <section className={cn('flex flex-col gap-3', className)} aria-label={title}>
      {!hideHeader ? (
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2 overflow-hidden">
            {icon ? <Icon name={icon} size={24} className="shrink-0 text-text-primary" /> : null}
            <h2 className="truncate text-base font-bold leading-6 text-text-primary">{title}</h2>
          </div>
          {seeAllTo ? (
            <Link
              to={seeAllTo}
              className="shrink-0 rounded-full bg-status-warning px-3 py-1.5 text-xs font-bold text-bg-elevated"
            >
              See all &gt;
            </Link>
          ) : null}
        </div>
      ) : null}
      {isLoading && games.length === 0 ? (
        <div className="flex h-40 items-center justify-center">
          <Spinner label="Loading games" />
        </div>
      ) : (
        <HorizontalScrollRow
          aria-label={title}
          withDesktopArrows
          className="-mx-1 gap-3 px-1 pb-1 pl-0"
        >
          {games.map((game) => (
            <div key={`${game.ProviderGameId}-${game.GameTag}`} role="listitem">
              <GameTile
                game={game}
                categoryOverride={categoryOverride}
                showFavorite={showFavorite}
                onToggleFavorite={onToggleFavorite}
                tone={tone}
                className="w-[108px] shrink-0 lg:w-[180px]"
              />
            </div>
          ))}
        </HorizontalScrollRow>
      )}
    </section>
  );
}
